import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import TeeBadge from "@/components/ui/tee-badge";
import { ArrowDown } from "lucide-react";
import { TeeButton } from "@/components/ui/tee-button";
import { TeeLink } from "@/components/ui/tee-link";
import { TeeLoader } from "@/components/ui/tee-loader";
import { useAssistantOptional } from "@/contexts/AssistantContext";
import { useIsMobile } from "@/hooks/useBreakpoint";
import { useAssistantRefs } from "@/hooks/useAssistantRefs";
import type {
  AssistantStep,
  AssistantUiMessageDto,
  PendingActionCardDto,
} from "@/types/support-assistant";
import { AssistantFeedback } from "./AssistantFeedback";
import { AssistantLinks } from "./AssistantLinks";
import { AssistantMessageText } from "./AssistantMessageText";
import { ClarifyChips } from "./ClarifyChips";
import { ClarifySearchList } from "./ClarifySearchList";
import { ConfirmationCard } from "./ConfirmationCard";
import { NoticeBanner } from "./NoticeBanner";

export interface MessageThreadProps {
  messages: AssistantUiMessageDto[];
  isStreaming: boolean;
  resolvingCardIds: string[];
  canSeeTickets?: boolean;
  onApprove: (cardId: string) => void;
  onReject: (cardId: string) => void;
  onAskAgain: (card: PendingActionCardDto) => void;
  onViewTicket?: (ticketId: string) => void;
  onChipSelect: (label: string) => void;
  readOnly?: boolean;
  /** Who is looking at the thread; changes requester caption wording. */
  perspective?: "requester" | "hq";
  /**
   * Extra rows (e.g. ticket activity) interleaved into the log by timestamp.
   * Optional and additive — AssistantPanel never passes this, so its render
   * is unaffected. Rows with an unparseable/missing `at` sort to the end.
   */
  timelineRows?: { key: string; at: string; node: ReactNode }[];
}

const NEAR_BOTTOM_PX = 48;
const SERVER_MESSAGE_ID_RE = /^[a-f0-9]{24}$/;

const stepsOf = (m: AssistantUiMessageDto): AssistantStep[] =>
  m.steps && m.steps.length > 0
    ? m.steps
    : m.toolActivity.map((tool) => ({
        id: tool.toolUseId,
        label: tool.label,
        status: tool.status === "denied" ? "failed" : tool.status,
      }));

function StepLine({ step }: { step: AssistantStep }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-[13px] text-[var(--text-content-sub-title)]">
      {step.status === "running" ? (
        <TeeLoader size="sm" />
      ) : step.status === "done" ? (
        <Check
          aria-label={t("assistant.toolDone")}
          className="h-4 w-4 shrink-0 text-[var(--color-forest)]"
        />
      ) : (
        <span aria-hidden="true" className="inline-block h-4 w-4 shrink-0" />
      )}
      <span>{step.label}</span>
    </div>
  );
}

function StepSummary({ steps }: { steps: AssistantStep[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const done = steps.filter((s) => s.status === "done");
  const label = (done.length > 0 ? done : steps).map((s) => s.label).join(", ");
  return (
    <div className="flex flex-col gap-1">
      <TeeButton
        variant="ghost"
        size="s"
        aria-expanded={open}
        aria-label={`${open ? t("assistant.thread.stepsHide") : t("assistant.thread.stepsShow")}: ${label}`}
        className="h-auto justify-start gap-1.5 px-0 py-1 text-[12px]"
        onClick={() => setOpen((v) => !v)}
        leftIcon={
          <Check
            aria-hidden="true"
            className="h-3.5 w-3.5 text-[var(--color-forest)]"
          />
        }
        text={<span className="truncate">{label}</span>}
      />
      {open && (
        <div className="flex flex-col gap-1 pl-1">
          {steps.map((s) => (
            <StepLine key={s.id} step={s} />
          ))}
        </div>
      )}
    </div>
  );
}

export function MessageThread({
  messages,
  isStreaming,
  resolvingCardIds,
  canSeeTickets,
  onApprove,
  onReject,
  onAskAgain,
  onViewTicket,
  onChipSelect,
  readOnly = false,
  perspective = "requester",
  timelineRows,
}: MessageThreadProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const assistant = useAssistantOptional();
  const setRefsVisible = assistant?.setRefsVisible;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);
  const [latestEl, setLatestEl] = useState<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinned) el.scrollTop = el.scrollHeight;
  }, [messages, pinned]);

  const latest = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "assistant" && m.refs && m.refs.length > 0) return m;
    }
    return undefined;
  }, [messages]);
  const latestId = latest?.id;

  useEffect(() => {
    if (!latestEl || readOnly) {
      setInView(false);
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setInView(entry.isIntersecting);
      },
      { root: scrollRef.current, threshold: 0.1 },
    );
    observer.observe(latestEl);
    return () => observer.disconnect();
  }, [latestEl, latestId, readOnly]);

  const refsActive = inView && !readOnly && !!latest;
  const { pulse, reveal } = useAssistantRefs(latest?.refs, refsActive);

  useEffect(() => {
    setRefsVisible?.(refsActive);
  }, [refsActive, setRefsVisible]);
  useEffect(() => () => setRefsVisible?.(false), [setRefsVisible]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setPinned(
      el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX,
    );
  };

  const jump = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setPinned(true);
  };

  const lastIndex = messages.length - 1;

  // Sort a copy ascending by parsed timestamp; unparseable/missing `at`
  // sorts last so it still renders (at the end) instead of being dropped.
  const sortedTimelineRows = useMemo(() => {
    if (!timelineRows || timelineRows.length === 0) return [];
    return [...timelineRows].sort((a, b) => {
      const ta = Date.parse(a.at);
      const tb = Date.parse(b.at);
      const naA = Number.isNaN(ta);
      const naB = Number.isNaN(tb);
      if (naA && naB) return 0;
      if (naA) return 1;
      if (naB) return -1;
      return ta - tb;
    });
  }, [timelineRows]);

  // Cursor walked forward once per render across the messages.map below —
  // emits every not-yet-emitted row whose timestamp is strictly before the
  // current message's createdAt, then flushes whatever remains at the end.
  let rowCursor = 0;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label={t("assistant.threadLabel")}
        className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[var(--background-page-body)] ${
          isMobile ? "p-4" : "p-[18px]"
        }`}
      >
        {messages.map((m, idx) => {
          const streamingThis =
            isStreaming && idx === lastIndex && m.role === "assistant";

          const mTime = Date.parse(m.createdAt);
          const rowsBefore: typeof sortedTimelineRows = [];
          if (!Number.isNaN(mTime)) {
            while (
              rowCursor < sortedTimelineRows.length &&
              !Number.isNaN(Date.parse(sortedTimelineRows[rowCursor].at)) &&
              Date.parse(sortedTimelineRows[rowCursor].at) < mTime
            ) {
              rowsBefore.push(sortedTimelineRows[rowCursor]);
              rowCursor += 1;
            }
          }

          const messageNode = renderMessage(m, idx, streamingThis);

          return (
            <Fragment key={m.id}>
              {rowsBefore.map((row) => (
                <Fragment key={row.key}>{row.node}</Fragment>
              ))}
              {messageNode}
            </Fragment>
          );
        })}
        {rowCursor < sortedTimelineRows.length &&
          sortedTimelineRows
            .slice(rowCursor)
            .map((row) => <Fragment key={row.key}>{row.node}</Fragment>)}
      </div>
      {!pinned && (
        <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[var(--background-card)] shadow-[0_2px_8px_rgba(22,36,30,0.18)]">
          <TeeButton
            variant="secondary"
            size="s"
            className="rounded-full"
            onClick={jump}
            leftIcon={<ArrowDown size={14} aria-hidden="true" />}
            text={t("assistant.jumpToLatest")}
          />
        </div>
      )}
    </div>
  );

  // Extracted so the messages.map above can interleave timelineRows without
  // disturbing any of the existing per-role rendering logic below.
  function renderMessage(
    m: AssistantUiMessageDto,
    idx: number,
    streamingThis: boolean,
  ): ReactNode {
    if (m.requesterReply) {
      const rr = m.requesterReply;
      const isHq = perspective === "hq";
      if (rr.event === "closed") {
        return (
          <p
            key={m.id}
            className="text-center text-[12px] text-[var(--text-content-sub-title)]"
          >
            {isHq
              ? t("assistant.thread.requesterClosed", { name: rr.byName })
              : t("assistant.thread.youClosed")}
          </p>
        );
      }
      return (
        <div key={m.id} className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
              {isHq
                ? t("assistant.thread.requesterLabel", { name: rr.byName })
                : t("assistant.thread.sentToHq")}
            </span>
            <span className="text-[12px] text-[var(--text-content-sub-title)]">
              {new Date(rr.at).toLocaleString()}
            </span>
          </div>
          <div className="max-w-[82%] rounded-[12px_12px_4px_12px] bg-[var(--background-card-elevated)] px-[13px] py-[9px] text-[14px] leading-[1.5]">
            <p className="whitespace-pre-wrap">{m.text}</p>
          </div>
        </div>
      );
    }

    if (m.role === "system") {
      if (m.hqReply) {
        return (
          <div
            key={m.id}
            aria-label={t("assistant.thread.hqReplyLabel")}
            className="flex max-w-[92%] flex-col gap-1 self-start rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] px-3 py-[10px]"
          >
            <div className="flex flex-wrap items-center gap-2">
              {m.hqReply.event === "closed" ? (
                <TeeBadge
                  type="green"
                  size="s"
                  label={t("assistant.thread.ticketClosed")}
                />
              ) : (
                <TeeBadge
                  type="grey-outline"
                  size="s"
                  label={t("assistant.thread.hqReplied")}
                />
              )}
              {m.hqReply.ticketNumber && (
                <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                  {m.hqReply.ticketNumber}
                </span>
              )}
              <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
                {t("assistant.thread.hqReply", {
                  name: m.hqReply.byName,
                })}
              </span>
              <span className="text-[12px] text-[var(--text-content-sub-title)]">
                {new Date(m.hqReply.at).toLocaleString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-[14px] leading-[1.55] text-[var(--text-content-title)]">
              {m.text}
            </p>
            {m.hqReply.attachments && m.hqReply.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {m.hqReply.attachments.map((a, i) =>
                  !a.url ? (
                    <span
                      key={i}
                      className="text-[12px] text-[var(--text-content-sub-title)]"
                    >
                      {a.name}
                    </span>
                  ) : a.contentType.startsWith("image/") ? (
                    <TeeLink key={i} href={a.url} external aria-label={a.name}>
                      <img
                        src={a.url}
                        alt={a.name}
                        className="max-h-40 max-w-full rounded-[6px] border border-[var(--border-default)]"
                      />
                    </TeeLink>
                  ) : (
                    <TeeLink key={i} href={a.url} external size="s">
                      {a.name}
                    </TeeLink>
                  ),
                )}
              </div>
            )}
          </div>
        );
      }
      if (m.scopeChange) {
        return (
          <p
            key={m.id}
            className="text-center text-[12px] text-[var(--text-content-sub-title)]"
          >
            {m.scopeChange.type && m.scopeChange.label
              ? t("assistant.thread.nowViewing", {
                  entity: m.scopeChange.label,
                })
              : t("assistant.thread.noContext")}
          </p>
        );
      }
      return m.notice ? (
        <NoticeBanner
          key={m.id}
          code={m.notice.code}
          retryAfter={m.notice.retryAfter}
          ticketNumber={m.notice.ticketNumber}
        />
      ) : null;
    }

    if (m.role === "user") {
      return (
        <div key={m.id} className="flex justify-end">
          <div className="max-w-[82%] rounded-[12px_12px_4px_12px] bg-[var(--background-card-elevated)] px-[13px] py-[9px] text-[14px] leading-[1.5]">
            <AssistantMessageText text={m.text} />
          </div>
        </div>
      );
    }

    const steps = stepsOf(m);
    const hasText = m.text.length > 0;
    const isLatestRefs = m.id === latestId;
    return (
      <div
        key={m.id}
        ref={isLatestRefs ? setLatestEl : undefined}
        className="flex flex-col items-start gap-2"
      >
        {steps.length > 0 &&
          (streamingThis ? (
            <div className="flex flex-col gap-1.5">
              {steps.map((s) => (
                <StepLine key={s.id} step={s} />
              ))}
            </div>
          ) : (
            <StepSummary steps={steps} />
          ))}
        {streamingThis && !hasText && (
          <div
            className="flex w-full flex-col gap-2"
            aria-label={t("assistant.thread.working")}
          >
            <div className="h-[10px] w-[92%] rounded-[4px] bg-[var(--background-card-elevated)] motion-safe:animate-pulse" />
            <div className="h-[10px] w-[74%] rounded-[4px] bg-[var(--background-card-elevated)] motion-safe:animate-pulse" />
          </div>
        )}
        {hasText && (
          <AssistantMessageText
            text={m.text}
            caret={streamingThis}
            refs={isLatestRefs ? m.refs : undefined}
            onRefFocus={isLatestRefs && refsActive ? pulse : undefined}
            onRefActivate={isLatestRefs && refsActive ? reveal : undefined}
          />
        )}
        {m.stopped && (
          <span className="text-[12px] font-semibold text-[var(--text-content-sub-title)]">
            {t("assistant.stopped")}
          </span>
        )}
        {!readOnly &&
          hasText &&
          !streamingThis &&
          SERVER_MESSAGE_ID_RE.test(m.id) && (
            <AssistantFeedback messageId={m.id} value={m.feedback} />
          )}
        {!readOnly && Array.isArray(m.links) && m.links.length > 0 && (
          <AssistantLinks links={m.links} disabled={isStreaming} />
        )}
        {m.clarify?.searchOptions && m.clarify.searchOptions.length > 0 && (
          <ClarifySearchList
            options={m.clarify.searchOptions}
            disabled={readOnly || isStreaming || idx !== lastIndex}
            onSelect={onChipSelect}
          />
        )}
        {m.clarify && m.clarify.options.length > 0 && (
          <ClarifyChips
            options={m.clarify.options}
            disabled={readOnly || isStreaming || idx !== lastIndex}
            onSelect={onChipSelect}
          />
        )}
        {m.card && (
          <div className="w-full">
            <ConfirmationCard
              card={m.card}
              resolving={resolvingCardIds.includes(m.card.id)}
              canSeeTickets={canSeeTickets}
              onApprove={onApprove}
              onReject={onReject}
              onAskAgain={onAskAgain}
              onViewTicket={onViewTicket}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
    );
  }
}
